import {
  type AcceptReviewerShiftChangesMutation,
  type AcceptReviewerShiftChangesMutationVariables,
  type AddOpenShiftsMutation,
  type AddOpenShiftsMutationVariables,
  type AutoDeskAssignmentMutation,
  type AutoDeskAssignmentMutationVariables,
  type BackupSchedule,
  type ClearMasterShiftsTeamAndPeriodMutationVariables,
  type ClearUserShiftsMutationVariables,
  type ClearWorkforceForTeamPeriodMutation,
  type ClearWorkforceForTeamPeriodMutationVariables,
  type CreateLiveShiftsForUserMutationVariables,
  type CreateManyTeamUserDefaultsMutation,
  type CreateManyTeamUserDefaultsMutationVariables,
  type CreateReviewerScheduleMutation,
  type CreateReviewerScheduleMutationVariables,
  type CreateShiftsForUserTeamPeriodMutationVariables,
  type CreateUserMutation,
  type CreateUserMutationVariables,
  type CreateWorkforceForTeamPeriodMutation,
  type CreateWorkforceForTeamPeriodMutationVariables,
  type DeleteLiveShiftsForUserMutation,
  type DeleteLiveShiftsForUserMutationVariables,
  type DeleteShiftsForSelectedUserTeamPeriodMutationVariables,
  type DeleteTeamUserDefaultMutation,
  type DeleteTeamUserDefaultMutationVariables,
  type DeleteUserScheduleNoteMutation,
  type DeleteUserScheduleNoteMutationVariables,
  type DeleteUserShiftsMutationVariables,
  GetAllUsersDocument,
  type GetAllUsersQuery,
  type PublishLiveScheduleMutation,
  type PublishLiveScheduleMutationVariables,
  type RecreateReviewerScheduleMutation,
  type RecreateReviewerScheduleMutationVariables,
  type RestoreBackupScheduleMutationVariables,
  type SaveBankedHoursRequestedMutationVariables,
  type SaveDeskAssignmentMutation,
  type SaveDeskAssignmentMutationVariables,
  type SaveDeskConfigMutation,
  type SaveDeskConfigMutationVariables,
  type SaveDeskConfigTemplateMutation,
  type SaveDeskConfigTemplateMutationVariables,
  type SaveSchedModMutationVariables,
  type Schedule,
  ScheduleMainFieldsFragmentDoc,
  ScheduleType,
  type SetPriorityDaysMutation,
  type SetPriorityDaysMutationVariables,
  type ShiftTradeMutationVariables,
  type UnassignDeskAssignmentMutation,
  type UnassignDeskAssignmentMutationVariables,
  type UnpublishLiveScheduleMutationVariables,
  type UpdateOpenShiftsMutation,
  type UpdateOpenShiftsMutationVariables,
  type UpdateOrCreateUserScheduleNoteMutation,
  type UpdateOrCreateUserScheduleNoteMutationVariables,
  type UpdateUserMutationVariables,
  type UpdateUserShiftsRoleMutationVariables,
  type UploadBidsMutationVariables,
  type UpsertBidConfigMutationVariables,
  useFragment,
} from "owfsa-graphql";
import logger from "@/lib/logger";
import type { RecursivePartial } from "@/lib/typing/types";
import { gql } from "@urql/vue";
import { type Cache, cacheExchange } from "@urql/exchange-graphcache";
import { toast } from "vue-sonner";
import { getDateFromDayId, schedPeriodForDate } from "@/lib/schedPeriod.ts";

export default cacheExchange({
  // NOSONAR
  keys: {
    AppInfo: () => null,
    ConflictingShiftPair: () => null,
  },
  resolvers: {
    Query: {
      auditEntry(_parent, args, _cache, _info) {
        return { __typename: "AuditEntry", id: args.id };
      },
      backupSchedule(_parent, args, _cache, _info) {
        return { __typename: "BackupSchedule", id: args.id };
      },
      bidCode(_parent, args, _cache, _info) {
        return { __typename: "BidCode", id: args.id };
      },
      bidCodeShiftCodeMatch(_parent, args, _cache, _info) {
        return { __typename: "BidCodeShiftCodeMatch", id: args.id };
      },
      bidConfigBidCodeLink(_parent, args, _cache, _info) {
        return { __typename: "BidConfigBidCodeLink", id: args.id };
      },
      bidConfigShiftCodeLink(_parent, args, _cache, _info) {
        return { __typename: "BidConfigShiftCodeLink", id: args.id };
      },
      bidDesignates(_parent, args, _cache, _info) {
        return { __typename: "BidDesignates", id: args.id };
      },
      day(_parent, args, _cache, _info) {
        return { __typename: "Day", id: args.id };
      },
      minMaxUserHours(_parent, args, _cache, _info) {
        return { __typename: "MinMaxUserHours", id: args.id };
      },
      modificationOfDuties(_parent, args, _cache, _info) {
        return { __typename: "ModificationOfDuties", id: args.id };
      },
      leaveOfAbsence(_parent, args, _cache, _info) {
        return { __typename: "LeaveOfAbsence", id: args.id };
      },
      openShift(_parent, args, _cache, _info) {
        return { __typename: "OpenShift", id: args.id };
      },
      schedMod(_parent, args, _cache, _info) {
        return { __typename: "SchedMod", id: args.id };
      },
      schedPeriod(_parent, args, _cache, _info) {
        return { __typename: "SchedPeriod", id: args.id };
      },
      schedule(_parent, args, _cache, _info) {
        return { __typename: "Schedule", id: args.id };
      },
      shift(_parent, args, _cache, _info) {
        return { __typename: "Shift", id: args.id };
      },
      shiftCode(_parent, args, _cache, _info) {
        return { __typename: "ShiftCode", id: args.id };
      },
      shiftErrorCode(_parent, args, _cache, _info) {
        return { __typename: "ShiftErrorCode", id: args.id };
      },
      startTimeModRule(_parent, args, _cache, _info) {
        return { __typename: "StartTimeModRule", id: args.id };
      },
      team(_parent, args, _cache, _info) {
        return { __typename: "Team", id: args.id };
      },
      teamUserDefault(_parent, args, _cache, _info) {
        return { __typename: "TeamUserDefault", id: args.id };
      },
      user(_parent, args, _cache, _info) {
        return { __typename: "User", id: args.id };
      },
      userBid(_parent, args, _cache, _info) {
        return { __typename: "UserBid", id: args.id };
      },
      userScheduleNote(_parent, args, _cache, _info) {
        return { __typename: "UserScheduleNote", id: args.id };
      },
      workforce(_parent, args, _cache, _info) {
        return { __typename: "Workforce", id: args.id };
      },
      workforceDetail(_parent, args, _cache, _info) {
        return { __typename: "WorkforceDetail", id: args.id };
      },
    },
  },
  updates: {
    Mutation: {
      acceptReviewerShiftChanges: (
        _result: AcceptReviewerShiftChangesMutation,
        args: AcceptReviewerShiftChangesMutationVariables,
        cache,
        _info,
      ) => {
        const schedule = cache.readFragment(
          gql`
            fragment _ on Schedule {
              id
              team {
                __typename
                id
              }
              schedPeriod {
                __typename
                id
              }
            }
          `,
          { id: args.scheduleId },
        ) as RecursivePartial<Schedule> | null;
        if (!schedule?.team?.id || !schedule?.schedPeriod?.id) {
          toast.error("Schedule not found! Failed to update shifts in cache.");
        }
      },

      assignDesignate: (_result, _args, cache, _info) => {
        cache.invalidate("Query", "assignedDesignate");
      },

      publishLiveSchedule: (
        result: PublishLiveScheduleMutation,
        args: PublishLiveScheduleMutationVariables,
        cache,
        _info,
      ) => {
        const schedule = useFragment(
          ScheduleMainFieldsFragmentDoc,
          result.publishLiveSchedule,
        );
        cache.link(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.LIVE,
          },
          {
            __typename: "Schedule",
            id: schedule.id,
          },
        );
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );
      },

      unpublishLiveSchedule: (
        _result,
        args: UnpublishLiveScheduleMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );

        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.LIVE,
          },
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });
      },

      updateOrCreateUserScheduleNote: (
        _result: UpdateOrCreateUserScheduleNoteMutation,
        args: UpdateOrCreateUserScheduleNoteMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "allUsersSchedNotes", {
          scheduleId: args.input.scheduleId,
        });
      },

      deleteUserScheduleNote: (
        _result: DeleteUserScheduleNoteMutation,
        args: DeleteUserScheduleNoteMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "allUsersSchedNotes", {
          scheduleId: args.scheduleId,
        });
      },

      createManyTeamUserDefaults: (
        _result: CreateManyTeamUserDefaultsMutation,
        args: CreateManyTeamUserDefaultsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "teamUserDefaultsByTeam", {
          teamId: args.teamId,
        });
      },

      deleteTeamUserDefault: (
        _result: DeleteTeamUserDefaultMutation,
        args: DeleteTeamUserDefaultMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "teamUserDefaultsByTeam", {
          teamId: args.teamId,
        });
      },

      createReviewerSchedule: (
        result: CreateReviewerScheduleMutation,
        args: CreateReviewerScheduleMutationVariables,
        cache,
        _info,
      ) => {
        const schedule = useFragment(
          ScheduleMainFieldsFragmentDoc,
          result.createReviewerSchedule,
        );
        cache.link(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: args.scheduleType,
          },
          {
            __typename: "Schedule",
            id: schedule.id,
          },
        );
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: args.scheduleType,
        });
        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: args.scheduleType,
        });
        cache.invalidate("Query", "reviewerSchedulesByTeamAndSchedPeriod", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      recreateReviewerSchedule: (
        result: RecreateReviewerScheduleMutation,
        args: RecreateReviewerScheduleMutationVariables,
        cache,
        _info,
      ) => {
        const schedule = useFragment(
          ScheduleMainFieldsFragmentDoc,
          result.recreateReviewerSchedule,
        );
        cache.link(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: args.scheduleType,
          },
          {
            __typename: "Schedule",
            id: schedule.id,
          },
        );
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: args.scheduleType,
        });
        cache.invalidate("Query", "reviewerSchedulesByTeamAndSchedPeriod", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: args.scheduleType,
        });
      },

      updateTeam: (_result, _args, _cache, _info) => {
        logger.debug("Caught updateTeam in cache");
      },

      /**
       * When creating a user,
       * add the new user to the existing list of users in the cache with an updater
       */
      createUser: (
        result: CreateUserMutation,
        _args: CreateUserMutationVariables,
        cache,
        _info,
      ) => {
        cache.updateQuery({ query: GetAllUsersDocument }, (data) => {
          if (data?.users) {
            return {
              users: [...data.users, result.createUser],
            } as GetAllUsersQuery;
          } else {
            return data;
          }
        });
      },

      /**
       * When deleting a user,
       * invalidate all teams' user defaults
       */
      updateUser: (
        _result,
        args: UpdateUserMutationVariables,
        cache,
        _info,
      ) => {
        if (args.input.isDeleted === true) {
          for (const args of cache
            .inspectFields({ __typename: "Query" })
            .filter((field) => field.fieldName === "teamUserDefaultsByTeam")
            .map((field) => field.arguments)) {
            cache.invalidate("Query", "teamUserDefaultsByTeam", args);
          }
        }
      },

      /**
       * When updating user's role on schedule,
       * invalidate teams' user defaults of the teamId
       * invalidate bids after being cleared here
       */
      updateUserShiftsRole: (
        _result,
        args: UpdateUserShiftsRoleMutationVariables,
        cache,
        _info,
      ) => {
        cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "userBidsInSchedule")
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
        cache.invalidate("Query", "teamUserDefaultsByTeam", {
          teamId: args.teamId,
        });
      },

      updateTeamUserDefault: (_result, _args, _cache, _info) => {
        logger.debug("Caught updateTeamUserDefault in cache");
      },

      updateEventBid: (_result, _args, cache, _info) => {
        invalidateBidData(cache);
      },

      updateBid: (_result, _args, cache, _info) => {
        cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "userBidsInSchedule")
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
      },

      updateBidsByBidder: (_result, _args, cache, _info) => {
        invalidateBidData(cache);
      },

      updateGdofShifts: (_result, _args, cache, _info) => {
        invalidateBidData(cache);
      },

      updateWorkforcesRequirement: (_result, _args, _cache, _info) => {
        logger.debug("Caught updateWorkforcesRequirement mutation in cache");
      },

      saveDeskConfigTemplate: (
        _result: SaveDeskConfigTemplateMutation,
        args: SaveDeskConfigTemplateMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "deskConfigTemplate", {
          teamId: args.teamId,
        });
      },

      saveDeskConfig: (
        _result: SaveDeskConfigMutation,
        args: SaveDeskConfigMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "deskConfigsForSchedule", {
          schedPeriodId: args.schedPeriodId,
          teamId: args.teamId,
        });
      },

      saveDeskAssignment: (
        _result: SaveDeskAssignmentMutation,
        args: SaveDeskAssignmentMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "deskAssignmentsForSchedule", {
          schedPeriodId: args.schedPeriodId,
          teamId: args.teamId,
        });
      },

      unassignDeskAssignment: (
        _result: UnassignDeskAssignmentMutation,
        args: UnassignDeskAssignmentMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "deskAssignmentsForSchedule", {
          schedPeriodId: args.schedPeriodId,
          teamId: args.teamId,
        });
      },

      autoDeskAssignment: (
        _result: AutoDeskAssignmentMutation,
        args: AutoDeskAssignmentMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "deskAssignmentsForSchedule", {
          schedPeriodId: args.schedPeriodId,
          teamId: args.teamId,
        });
      },

      /**
       * When restoring a backup schedule,
       * invalidate corresponding schedule's data cache
       */
      restoreBackupSchedule: (
        _result,
        args: RestoreBackupScheduleMutationVariables,
        cache,
        _info,
      ) => {
        const backupSchedule = cache.readFragment(
          gql`
            fragment _ on BackupSchedule {
              schedule {
                id
                team {
                  id
                }
                schedPeriod {
                  id
                }
              }
            }
          `,
          { id: args.scheduleId },
        ) as RecursivePartial<BackupSchedule> | null;

        if (
          !backupSchedule?.schedule?.team?.id ||
          !backupSchedule.schedule?.schedPeriod?.id
        ) {
          toast.error(
            "Schedule backup not found! Failed to invalidate schedule in cache.",
          );
          return;
        }

        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: backupSchedule.schedule.team.id,
            schedPeriodId: backupSchedule.schedule.schedPeriod.id,
            scheduleType: ScheduleType.MASTER,
          },
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: backupSchedule.schedule.team.id,
          schedPeriodId: backupSchedule.schedule.schedPeriod.id,
          scheduleType: ScheduleType.MASTER,
        });
      },

      /**
       * When creating all shifts for a schedule, no need to invalidate anything.
       * When creating shifts for a user, need to invalidate shifts cache and more.
       */
      createShiftsForUserTeamPeriod: (
        _result,
        args: CreateShiftsForUserTeamPeriodMutationVariables,
        cache,
        _info,
      ) => {
        // get new schedule with all users
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );

        // get the new shifts for the new user for any sched type
        cache
          .inspectFields("Query")
          .filter(
            (field) =>
              field.fieldName === "shiftsByTeamAndSchedPeriodAndScheduleType" &&
              field.arguments?.teamId === args.teamId &&
              field.arguments?.schedPeriodId === args.schedPeriodId,
          )
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });

        // refetch user bids for schedule
        invalidateBidData(cache);

        // allow flipping to bid entry page for testing bid configs
        cache.invalidate("Query", "bidConfigsForUser");

        // we need to show the new users shifts from other teams
        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
      },

      /**
       * When a new user is added to Live schedule, we need to invalidate shifts cache.
       * When creating shifts for a user, need to invalidate shifts cache and more.
       */
      createLiveShiftsForUser: (
        _result,
        args: CreateLiveShiftsForUserMutationVariables,
        cache,
        _info,
      ) => {
        // get the new shifts for the new user
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });

        // we need to show the new users shifts from other teams
        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });

        cache.invalidate("Query", "schedModsForSchedule", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      createWorkforceForTeamPeriod: (
        _result: CreateWorkforceForTeamPeriodMutation,
        args: CreateWorkforceForTeamPeriodMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "workforcesByTeamAndSchedPeriod", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      clearWorkforceForTeamPeriod: (
        _result: ClearWorkforceForTeamPeriodMutation,
        args: ClearWorkforceForTeamPeriodMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "workforcesByTeamAndSchedPeriod", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      setPriorityDays: (
        _result: SetPriorityDaysMutation,
        args: SetPriorityDaysMutationVariables,
        cache,
        _info,
      ) => {
        const schedPeriodIds = (
          Array.isArray(args.priorityDays)
            ? args.priorityDays
            : [args.priorityDays]
        ).reduce(
          (
            acc: Set<string>,
            day: { dayId: number; isPriorityDay: boolean },
          ) => {
            const schedPeriodId = schedPeriodForDate(
              getDateFromDayId(day.dayId),
            );
            acc.add(schedPeriodId);
            return acc;
          },
          new Set<string>(),
        );

        for (const schedPeriodId of schedPeriodIds) {
          cache.invalidate("Query", "workforcesByTeamAndSchedPeriod", {
            teamId: args.teamId,
            schedPeriodId,
          });
        }
      },

      addOpenShifts: (
        _result: AddOpenShiftsMutation,
        args: AddOpenShiftsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "openShiftsForLiveSched", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      updateOpenShifts: (
        _result: UpdateOpenShiftsMutation,
        args: UpdateOpenShiftsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "openShiftsForLiveSched", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
        });
      },

      deleteBackupSchedule(_result, args, cache, _info) {
        cache.invalidate("Query", "backupSchedules");
      },

      /**
       * When saving leave of absence for a user,
       * invalidate all schedules' shifts cache
       */
      saveLeaveOfAbsenceByUser: (_result, _args, cache, _info) => {
        cache
          .inspectFields("Query")
          .filter(
            (field) =>
              field.fieldName === "shiftsByTeamAndSchedPeriodAndScheduleType",
          )
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
      },

      /**
       * When resetting bids for a user,
       * invalidate all schedules' shifts cache
       */
      resetBids: (_result, _args, cache, _info) => invalidateBidData(cache),

      /**
       * When saving mods for a user,
       * invalidate all schedules' shifts cache
       */
      saveModificationOfDutiesByUser: (_result, _args, cache, _info) => {
        cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "users")
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
      },

      /**
       * When generating schedule,
       * invalidate all schedules' shifts cache
       */
      generateScheduleV2: (_result, _args, cache, _info) => {
        cache
          .inspectFields("Query")
          .filter(
            (field) =>
              field.fieldName === "shiftsByTeamAndSchedPeriodAndScheduleType",
          )
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
      },

      /**
       * When deleting user shifts,
       * invalidate current schedule's data cache
       */
      deleteUserShifts: (
        _result,
        args: DeleteUserShiftsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });

        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
      },

      /**
       * When deleting user shifts for selected user team period,
       * invalidate current schedule's shifts cache
       */
      deleteShiftsForSelectedUserTeamPeriod: (
        _result,
        args: DeleteShiftsForSelectedUserTeamPeriodMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });

        cache.invalidate("Query", "externalShifts", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
      },

      /**
       * When deleting user live shifts for selected user team period,
       * invalidate current schedule's shifts cache
       */
      deleteLiveShiftsForUser: (
        _result: DeleteLiveShiftsForUserMutation,
        args: DeleteLiveShiftsForUserMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });
      },

      /**
       * When uploading bids,
       * invalidate current schedule's shifts cache
       */
      uploadBids: (
        _result,
        args: UploadBidsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate(
          "Query",
          "scheduleByTeamAndSchedPeriodAndScheduleType",
          {
            teamId: args.teamId,
            schedPeriodId: args.schedPeriodId,
            scheduleType: ScheduleType.MASTER,
          },
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });

        cache.invalidate("Query", "teamUserDefaultsByTeam", {
          teamId: args.teamId,
        });
      },

      /**
       * When deleting master schedule shifts,
       * invalidate current schedule's data cache
       */
      clearMasterShiftsTeamAndPeriod: (
        _result,
        args: ClearMasterShiftsTeamAndPeriodMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
      },

      clearMasterUserBids: (_result, _args, cache, _info) => {
        cache
          .inspectFields("Query")
          .filter((field) => field.fieldName === "userBidsInSchedule")
          .forEach((field) => {
            cache.invalidate("Query", field.fieldKey);
          });
      },

      /**
       * When clearing user shift codes from their shifts,
       * invalidate the shift's cache
       */
      clearUserShifts: (
        _result,
        args: ClearUserShiftsMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.teamId,
          schedPeriodId: args.schedPeriodId,
          scheduleType: ScheduleType.MASTER,
        });
      },

      saveSchedConfigOverride: (
        _result,
        args: UpsertBidConfigMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "schedConfigOverrides", {
          schedPeriodId: args.input.schedPeriodId,
        });
      },

      saveBankedHoursRequested: (
        _result,
        args: SaveBankedHoursRequestedMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "schedConfigOverrides", {
          schedPeriodId: args.input.schedPeriodId,
        });
      },

      upsertBidConfig: (
        _result,
        args: UpsertBidConfigMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "bidConfig", {
          schedPeriodId: args.input.schedPeriodId,
          shiftCodeIds: args.input.shiftCodeIds,
          userRole: args.input.userRole,
          teamId: args.input.teamId,
        });
      },

      saveSchedMod: (
        _result,
        args: SaveSchedModMutationVariables,
        cache,
        _info,
      ) => {
        cache.invalidate("Query", "schedModsForSchedule", {
          teamId: args.input.teamId,
          schedPeriodId: args.input.schedPeriodId,
        });

        // Invalidate externalShifts for ALL teams on this period (LIVE only).
        // Another team viewing this user as external needs the updated shift code.
        invalidateAllForPeriod(
          cache,
          "externalShifts",
          args.input.schedPeriodId,
          ScheduleType.LIVE,
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: args.input.teamId,
          schedPeriodId: args.input.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });

        cache.invalidate("Query", "deskAssignmentsForSchedule", {
          schedPeriodId: args.input.schedPeriodId,
          teamId: args.input.teamId,
        });

        cache.invalidate("Query", "openShiftsForLiveSched", {
          teamId: args.input.teamId,
          schedPeriodId: args.input.schedPeriodId,
        });
      },

      shiftTrade: (
        _result,
        args: ShiftTradeMutationVariables,
        cache,
        _info,
      ) => {
        const schedModInput = Array.isArray(args.inputs)
          ? args.inputs[0]
          : args.inputs;

        cache.invalidate("Query", "schedModsForSchedule", {
          teamId: schedModInput.teamId,
          schedPeriodId: schedModInput.schedPeriodId,
        });

        // Invalidate externalShifts for ALL teams on this period (LIVE only).
        // Another team viewing this user as external needs the updated shift code.
        invalidateAllForPeriod(
          cache,
          "externalShifts",
          schedModInput.schedPeriodId,
          ScheduleType.LIVE,
        );

        cache.invalidate("Query", "shiftsByTeamAndSchedPeriodAndScheduleType", {
          teamId: schedModInput.teamId,
          schedPeriodId: schedModInput.schedPeriodId,
          scheduleType: ScheduleType.LIVE,
        });

        cache.invalidate("Query", "deskAssignmentsForSchedule", {
          schedPeriodId: schedModInput.schedPeriodId,
          teamId: schedModInput.teamId,
        });

        cache.invalidate("Query", "openShiftsForLiveSched", {
          teamId: schedModInput.teamId,
          schedPeriodId: schedModInput.schedPeriodId,
        });
      },
    },
  },
});

function invalidateBidData(cache: Cache) {
  cache
    .inspectFields("Query")
    .filter(
      (field) =>
        field.fieldName === "userBidsForBidder",
    )
    .forEach((field) => {
      cache.invalidate("Query", field.fieldKey);
    });

  cache
    .inspectFields("Query")
    .filter(
      (field) =>
        field.fieldName === "shiftsForBidding",
    )
    .forEach((field) => {
      cache.invalidate("Query", field.fieldKey);
    });
}

/**
 * Invalidate every cached entry of `fieldName` that matches the given
 * schedPeriodId (and optional scheduleType).  Used to propagate sched-mod
 * changes across all teams that share the same schedule period.
 */
function invalidateAllForPeriod(
  cache: Cache,
  fieldName: string,
  schedPeriodId: string,
  scheduleType?: ScheduleType,
) {
  cache
    .inspectFields("Query")
    .filter((field) => {
      if (field.fieldName !== fieldName) {return false;}
      const fieldArgs = field.arguments as {
        schedPeriodId?: string;
        scheduleType?: ScheduleType;
      } | null;
      if (fieldArgs?.schedPeriodId !== schedPeriodId) {return false;}
      if (scheduleType !== undefined && fieldArgs?.scheduleType !== scheduleType) {return false;}
      return true;
    })
    .forEach((field) => {
      cache.invalidate("Query", field.fieldName, field.arguments);
    });
}
